/**
 * NPColimitationPage — Zhou 2019 dual-nutrient (N + P) co-limitation diagnostic
 *
 * Classical lake management assumes single-nutrient (usually P) limitation, but in eutrophic
 * Chinese reservoirs Zhou et al. (2019) showed that N and P co-limit during summer blooms.
 * The Liebig + multiplicative hybrid index:
 *     L_N = N / (N + K_N),    L_P = P / (P + K_P)
 *     L_min = min(L_N, L_P)              (Liebig)
 *     L_mult = L_N × L_P                  (Mitscherlich)
 *     L_used = 0.5 × (L_min + L_mult)     (Zhou blended)
 *
 * The ratio N:P (Redfield 16:1 by mole, ~7.2:1 by mass) identifies which nutrient is currently
 * limiting and informs whether to target N (denitrification) or P (capping) management.
 *
 * Reference: Zhou, J., Zhang, M., Lin, B. & Lu, P. (2019) "Lowland fluvial phosphorus altered
 *            by dams and seasonal nitrogen-phosphorus co-limitation of cyanobacterial blooms"
 *            Water Research 156: 251-262, Tsinghua University, State Key Lab of Hydroscience.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Sigma, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function NPColimitationPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('np_colimitation');
  const [tn, setTn] = useState(1.2);          // mg N/L
  const [tp, setTp] = useState(0.08);         // mg P/L
  const [kN, setKN] = useState(0.10);         // mg/L
  const [kP, setKP] = useState(0.010);        // mg/L

  const R = useMemo(() => {
    const L_N = tn / (tn + kN);
    const L_P = tp / (tp + kP);
    const L_min = Math.min(L_N, L_P);
    const L_mult = L_N * L_P;
    const L_used = 0.5 * (L_min + L_mult);
    const npRatio = tn / Math.max(tp, 1e-6);
    let regime: string;
    if (npRatio > 16) regime = 'P-limited';
    else if (npRatio < 5) regime = 'N-limited';
    else regime = 'co-limited';
    const colimited = regime === 'co-limited' || Math.abs(L_N - L_P) < 0.15;
    return { L_N, L_P, L_min, L_mult, L_used, npRatio, regime, colimited };
  }, [tn, tp, kN, kP]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';
  const regimeClasses =
    R.regime === 'co-limited'
      ? { border: 'border-amber-700', bg: 'bg-amber-900/15', label: 'text-amber-300', value: 'text-amber-200' }
      : R.regime === 'N-limited'
        ? { border: 'border-sky-700', bg: 'bg-sky-900/15', label: 'text-sky-300', value: 'text-sky-200' }
        : { border: 'border-fuchsia-700', bg: 'bg-fuchsia-900/15', label: 'text-fuchsia-300', value: 'text-fuchsia-200' };

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Sigma} title="Module — N + P Co-limitation Diagnostic"
        subtitle="Zhou 2019: dual-nutrient limitation index for cyanobacterial bloom forecasting"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('np_colimitation')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="np_colimitation" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">L_N (Monod for N)</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.L_N, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">TN = {tn} mg/L</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">L_P (Monod for P)</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.L_P, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">TP = {tp} mg/L</div>
        </div>
        <div className={`rounded-xl border-2 ${regimeClasses.border} ${regimeClasses.bg} p-4`}>
          <div className={`text-xs uppercase tracking-wider ${regimeClasses.label}`}>Regime</div>
          <div className={`text-lg font-bold ${regimeClasses.value} mt-1 uppercase`}>{R.regime}</div>
          <div className="text-xs text-slate-500 mt-1">N:P = {fmt(R.npRatio, 1)} (mass)</div>
        </div>
        <div className="rounded-xl border-2 border-red-700 bg-red-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-red-300">Blended L_used</div>
          <div className="text-2xl font-bold text-red-200 mt-1">{fmt(R.L_used, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">growth multiplier on µ_max</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">TN: <strong className="text-slate-100">{tn.toFixed(2)} mg/L</strong></label>
          <input type="range" min={0.05} max={5} step={0.05} value={tn} onChange={e => setTn(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">TP: <strong className="text-slate-100">{tp.toFixed(3)} mg/L</strong></label>
          <input type="range" min={0.005} max={0.5} step={0.005} value={tp} onChange={e => setTp(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">K_N: <strong className="text-slate-100">{kN.toFixed(2)} mg/L</strong></label>
          <input type="range" min={0.02} max={0.4} step={0.01} value={kN} onChange={e => setKN(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">K_P: <strong className="text-slate-100">{kP.toFixed(3)} mg/L</strong></label>
          <input type="range" min={0.002} max={0.05} step={0.001} value={kP} onChange={e => setKP(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
      </div>

      <div className={`rounded-xl border-2 ${R.colimited ? 'border-amber-700 bg-amber-900/15' : 'border-slate-700 bg-slate-900'} p-4 text-xs leading-relaxed`}>
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Management interpretation</div>
        {R.colimited ? (
          <p className="text-amber-100">Co-limitation regime detected: <strong>both N and P</strong> jointly control growth. Single-nutrient
          management (P-only capping or N-only denitrification) will give only a partial response. Combine aeration (suppresses
          internal P release AND raises nitrification) with cap or denitrification side-stream.</p>
        ) : R.regime === 'P-limited' ? (
          <p className="text-fuchsia-100">Phosphorus is the limiting nutrient (N:P &gt; 16). Aeration + sediment-capping (Chen 2017 module)
          will give the strongest response. Reducing TN further will have little effect on bloom magnitude.</p>
        ) : (
          <p className="text-sky-100">Nitrogen is the limiting nutrient (N:P &lt; 5). Cyanobacteria capable of N-fixation
          (<em>Anabaena</em>, <em>Aphanizomenon</em>) will dominate over green algae. Aeration's main benefit here is suppressing
          internal P loading to keep the system from flipping to P-limitation later in the season.</p>
        )}
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <p>The Algal Dynamics page consumes <strong>L_used</strong> as the multiplicative growth limitation factor when this module is enabled,
        replacing the single-nutrient Monod term. Half-saturation constants K_N and K_P default to literature values for freshwater
        cyanobacteria (Reynolds 2006) but can be tuned to in-situ measurements.</p>
        <p className="italic text-slate-500 mt-2">Reference: Zhou J., Zhang M., Lin B. &amp; Lu P. (2019) Water Research 156: 251-262, Tsinghua University, China.</p>
      </div>
    </div>
  );
}
